import React, { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { auth } from "./firebase";

const AuthContext = createContext(null);

// #region Constants — localStorage keys for token persistence, 5-min refresh buffer before expiry, support email fallback
const TOKEN_KEY = "firebaseIdToken";
const TOKEN_EXPIRY_KEY = "firebaseTokenExpiry";
const TOKEN_REFRESH_BUFFER = 5 * 60 * 1000; // Refresh 5 minutes before expiry
const SUPPORT_EMAIL =
  process.env.REACT_APP_SUPPORT_EMAIL || "support@matchmixer.co.uk";
// #endregion

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // #region Auth state init — on mount, restores session from localStorage if token is still valid; falls back to subscribing to Firebase auth state changes when no stored token exists
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if we have a stored token
        const storedToken = localStorage.getItem(TOKEN_KEY);
        const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

        if (storedToken && tokenExpiry) {
          const expiryTime = parseInt(tokenExpiry, 10);
          const currentTime = Date.now();

          // If token is still valid, use it
          if (currentTime < expiryTime) {
            // Verify token is still valid with Firebase
            try {
              const unsubscribe = onAuthStateChanged(
                auth,
                async (firebaseUser) => {
                  if (firebaseUser) {
                    const token = await firebaseUser.getIdToken();
                    localStorage.setItem(TOKEN_KEY, token);
                    setUser(firebaseUser);
                  } else {
                    localStorage.removeItem(TOKEN_KEY);
                    localStorage.removeItem(TOKEN_EXPIRY_KEY);
                    setUser(null);
                  }
                  setLoading(false);
                },
              );

              return () => unsubscribe();
            } catch (err) {
              localStorage.removeItem(TOKEN_KEY);
              localStorage.removeItem(TOKEN_EXPIRY_KEY);
              setLoading(false);
            }
          } else {
            // Token expired, clear storage
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(TOKEN_EXPIRY_KEY);
            setLoading(false);
          }
        } else {
          // No stored token, just monitor auth state
          const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            setUser(firebaseUser);
            setLoading(false);
          });

          return () => unsubscribe();
        }
      } catch (err) {
        console.error("Error initializing auth:", err);
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);
  // #endregion

  // #region Token auto-refresh — checks every 60 seconds whether the stored token is within TOKEN_REFRESH_BUFFER of expiry; force-refreshes and updates localStorage when it is
  useEffect(() => {
    if (!user) return;

    const refreshTokenIfNeeded = async () => {
      try {
        const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
        if (!tokenExpiry) return;

        const expiryTime = parseInt(tokenExpiry, 10);
        const currentTime = Date.now();
        const timeUntilExpiry = expiryTime - currentTime;

        // If token expires within buffer time, refresh it
        if (timeUntilExpiry < TOKEN_REFRESH_BUFFER) {
          const newToken = await user.getIdToken(true); // Force refresh
          const newExpiry = Date.now() + 60 * 60 * 1000; // 1 hour from now
          localStorage.setItem(TOKEN_KEY, newToken);
          localStorage.setItem(TOKEN_EXPIRY_KEY, newExpiry.toString());
        }
      } catch (err) {
        console.error("Error refreshing token:", err);
      }
    };

    // Check token every minute
    const interval = setInterval(refreshTokenIfNeeded, 60 * 1000);

    // Also refresh when user signs in
    refreshTokenIfNeeded();

    return () => clearInterval(interval);
  }, [user]);
  // #endregion

  // #region Auth methods — signUp and signIn (email/password), signInWithGoogle (OAuth popup), logout; all store the fresh token in localStorage on success
  const signUp = async (email, password) => {
    try {
      setError(null);
      const result = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const token = await result.user.getIdToken();
      const expiry = Date.now() + 60 * 60 * 1000; // 1 hour from now
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toString());
      setUser(result.user);
      return result.user;
    } catch (err) {
      const message =
        err.code === "auth/email-already-in-use"
          ? "Email already in use"
          : err.code === "auth/weak-password"
            ? "Password must be at least 6 characters"
            : err.message;
      setError(message);
      throw err;
    }
  };

  const signIn = async (email, password) => {
    try {
      setError(null);
      const result = await signInWithEmailAndPassword(auth, email, password);
      const token = await result.user.getIdToken();
      const expiry = Date.now() + 60 * 60 * 1000; // 1 hour from now
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toString());
      setUser(result.user);
      return result.user;
    } catch (err) {
      const message =
        err.code === "auth/user-not-found"
          ? "User not found"
          : err.code === "auth/wrong-password"
            ? "Incorrect password"
            : err.code === "auth/user-disabled"
              ? `Your account is currently disabled. If you believe this is a mistake, contact ${SUPPORT_EMAIL}.`
              : err.message;
      setError(message);
      throw err;
    }
  };

  const signInWithGoogle = async () => {
    try {
      setError(null);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const token = await result.user.getIdToken();
      const expiry = Date.now() + 60 * 60 * 1000; // 1 hour from now
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toString());
      setUser(result.user);
      return result.user;
    } catch (err) {
      const message =
        err.code === "auth/popup-closed-by-user"
          ? "Google sign-in was cancelled"
          : err.code === "auth/account-exists-with-different-credential"
            ? "An account already exists with the same email using another sign-in method"
            : err.code === "auth/user-disabled"
              ? `Your account is currently disabled. If you believe this is a mistake, contact ${SUPPORT_EMAIL}.`
              : err.message;
      setError(message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      sessionStorage.removeItem("mm_session_init");
      setUser(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };
  // #endregion

  // #region Provider and hook — AuthContext.Provider exposes user/loading/error and all auth methods; useAuth() is the consumer hook (throws if used outside AuthProvider)
  const value = {
    user,
    loading,
    error,
    signUp,
    signIn,
    signInWithGoogle,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
// #endregion
